import React, { useEffect } from 'react';
import { devLog } from './utils/devLogger';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AliveScope } from 'react-activation';
import { ToastContainer } from 'react-toastify';
import { Home, Login, FindEmail, FindPassword, SignUpStep1, SignUpStep2, NotFound, CMSLogin, DateTimePickerTest, DropdownTestPage, OngoingAuctions, BidRequest, LiveBidRequest, Notification, PaymentGuide, LiveWatch } from './pages';
import AppleCallbackPage from './pages/AppleCallbackPage';
import AdditionalInfo from './pages/AdditionalInfo';
import Auction from './pages/Auction';
import Favorites from './pages/Favorites';
import MyPage from './pages/MyPage';
import EditProfile from './pages/EditProfile';
import ChangePassword from './pages/ChangePassword';
import NotificationSettings from './pages/NotificationSettings';
import TermsView from './pages/TermsView';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PaymentManagement from './pages/PaymentManagement';
import LotHistory from './pages/LotHistory';
import LotDetail from './pages/LotDetail';
import PreBid from './pages/PreBid';
import LiveBid from './pages/LiveBid';
import BidPage from './pages/BidPage';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/Payment/PaymentSuccess';
import PaymentFail from './pages/Payment/PaymentFail';
import SearchPage from './pages/Search';
import AuctionIntroPage from './pages/AuctionIntro/AuctionIntroPage';
import ShoppingRequest from './pages/ShoppingRequest';
import AnimatedBorderButtonDemo from './components/AnimatedBorderButtonDemo';
import WonAuctionDetail from './pages/Auction/WonAuctionDetail';
import CMSLayout from './layout/CMSLayout';
import CMSTerms from './pages/CMS/Terms';
import AdminManagement from './pages/CMS/AdminManagement';
import UserManagement from './pages/CMS/UserManagement';
import RealTimeMonitoring from './pages/CMS/RealTimeMonitoring';
import NotificationManagement from './pages/CMS/NotificationManagement';
import CMSPaymentManagement from './pages/CMS/PaymentManagement';
import AuctioneerManagement from './pages/CMS/auction/auctioneers';
import AuctionManagement from './pages/CMS/auction/auctions';
import LotManagement from './pages/CMS/auction/lots';
import AuctioneerPage from './pages/Auctioneer/AuctioneerPage';
import ProtectedRoute from './components/ProtectedRoute';
import TokenRefreshOverlay from './components/TokenRefreshOverlay';
import ScrollToTop from './components/ScrollToTop';
import { DeviceProvider } from './context/DeviceContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { SignUpProvider } from './context/SignUpContext';
import { UserAuthProvider } from './context/UserAuthContext';
import { NotificationProvider } from './context/NotificationContext';
// Flutter 통신 테스트를 위한 import
import './utils/flutterTestHelper';
import { LangProvider } from './context/LangContext';
import { flutterBridge } from './utils/flutterBridge';
import { LayoutProvider } from './context/LayoutContext';
import { BidProvider } from './context/BidContext';
import TossPaymentDebug from './components/TossPaymentDebug';
// import './App.css';
// import 'react-toastify/dist/ReactToastify.css';

function App() {
  const [showTossDebug, setShowTossDebug] = React.useState(false);

  // Flutter 리프레시 토큰 리스너 등록
  useEffect(() => {
    devLog('🔄 Flutter 리프레시 토큰 리스너 등록');
    const cleanup = flutterBridge.setupRefreshTokenListener();
    
    return () => {
      cleanup();
      devLog('🔄 Flutter 리프레시 토큰 리스너 해제');
    };
  }, []);

  // 개발 모드에서 키보드 단축키로 디버그 패널 표시
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (process.env.NODE_ENV === 'development' && event.ctrlKey && event.shiftKey && event.key === 'T') {
        setShowTossDebug(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) {
      return;
    }

    const previousValue = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousValue;
    };
  }, []);

  return (
    <LangProvider>
      <DeviceProvider>
        <UserAuthProvider>
          <NotificationProvider>
            <SignUpProvider>
              <BidProvider>
                <LayoutProvider>
                    <TokenRefreshOverlay>
                      <Router>
                        <AliveScope>
                          <ScrollToTop />
                          <div className="App">            
                            <Routes>
                            <Route path="/" element={
                              // <KeepAlive cacheKey="home">
                                <Home />
                              // </KeepAlive>
                            } />
                          <Route path="/search" element={<SearchPage />} />
                          <Route path="/auction" element={<ProtectedRoute><Auction /></ProtectedRoute>} />
                          <Route path="/ongoing-auctions" element={<OngoingAuctions />} />
                          <Route path="/live-auctions" element={<OngoingAuctions />} />
                          <Route path="/lot-history" element={<ProtectedRoute><LotHistory /></ProtectedRoute>} />
                          <Route path="/lot-detail/:id" element={<LotDetail />} />
                          <Route path="/auctioneer/:auctioneerIndex" element={<AuctioneerPage />} />
                          <Route path="/auction-intro" element={<ProtectedRoute><AuctionIntroPage /></ProtectedRoute>} />
                          <Route path="/auction/:auctionId/intro" element={<AuctionIntroPage />} />
                          <Route path="/pre-bid/:id" element={<PreBid />} />
                          <Route path="/shopping-request/:id" element={<ShoppingRequest />} />
                          <Route path="/live-bid" element={<LiveBid />} />
                          <Route path="/live-watch" element={<LiveWatch />} />
                          <Route path="/bid-request/:lotId" element={<ProtectedRoute><BidRequest /></ProtectedRoute>} />
                          <Route path="/live-bid-request" element={<ProtectedRoute><LiveBidRequest /></ProtectedRoute>} />
                          <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
                          <Route path="/payment/success" element={<PaymentSuccess />} />
                          <Route path="/payment/fail" element={<PaymentFail />} />
                          <Route path="/payment-guide" element={<ProtectedRoute><PaymentGuide /></ProtectedRoute>} />
                          <Route path="/bid" element={<ProtectedRoute><BidPage /></ProtectedRoute>} />
                          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                          <Route path="/won-auction-detail/:auctionIndex" element={<ProtectedRoute><WonAuctionDetail /></ProtectedRoute>} />
                          <Route path="/mypage" element={<MyPage />} />
                          <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
                          <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
                          <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
                          <Route path="/notification" element={<ProtectedRoute><Notification /></ProtectedRoute>} />
                          <Route path="/payment-management" element={<ProtectedRoute><PaymentManagement /></ProtectedRoute>} />
                          <Route path="/terms" element={<TermsView />} />
                          <Route path="/privacy" element={<PrivacyPolicy />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/auth/apple/callback" element={<AppleCallbackPage />} />
                          <Route path="/find-email" element={<FindEmail />} />
                          <Route path="/find-password" element={<FindPassword />} />
                          <Route path="/signup" element={<SignUpStep1 />} />
                          <Route path="/signup/step2" element={<SignUpStep2 />} />
                          <Route path="/additional-info" element={<AdditionalInfo />} />
                          <Route path="/test/datetime-picker" element={<DateTimePickerTest />} />
                          <Route path="/test/dropdown" element={<DropdownTestPage />} />
                          <Route path="/test/animated-border" element={<AnimatedBorderButtonDemo />} />
                          
                          {/* CMS 전체 영역을 AdminAuthProvider로 감싸기 */}
                          <Route path="/cms/*" element={
                            <AdminAuthProvider>
                              <Routes>
                                <Route path="/login" element={<CMSLogin />} />
                                <Route path="/*" element={<CMSLayoutRoute />} />
                              </Routes>
                            </AdminAuthProvider>
                          } />
                          
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </div>
                    
                      {/* 전역 토스트 컨테이너 */}
                      <ToastContainer
                        position="top-center"
                        autoClose={3000}
                        hideProgressBar={false}
                        newestOnTop={false}
                        closeOnClick
                        rtl={false}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        style={{ zIndex: 10002 }}
                      />

                      {/* 개발 모드 토스 결제 디버그 패널 */}
                      {process.env.NODE_ENV === 'development' && showTossDebug && (
                        <TossPaymentDebug onClose={() => setShowTossDebug(false)} />
                      )}
                      
                      {/* 디버그 모드에서만 표시되는 Flutter 토큰 디버그 모달 */}
                      {/* <DebugTokenModal /> */}
                        </AliveScope>
                      </Router>
                    </TokenRefreshOverlay>
                </LayoutProvider>
              </BidProvider>
          </SignUpProvider>
        </NotificationProvider>
      </UserAuthProvider>
    </DeviceProvider>
  </LangProvider>
  );
}

// CMS 레이아웃이 적용되는 라우트들을 위한 컴포넌트
const CMSLayoutRoute = () => {
  return (
    <CMSLayout>
      <Routes>
        <Route path="/" element={<Navigate to="/cms/admin" replace />} />
        <Route path="/admin" element={<AdminManagement />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/auction" element={<Navigate to="/cms/auction/auctioneers" replace />} />
        <Route path="/auction/auctioneers" element={<AuctioneerManagement />} />
        <Route path="/auction/auctions" element={<AuctionManagement />} />
        <Route path="/auction/lots" element={<LotManagement />} />
        <Route path="/bidding" element={<RealTimeMonitoring />} />
        <Route path="/payment" element={<CMSPaymentManagement />} />
        <Route path="/notification" element={<NotificationManagement />} />
        <Route path="/terms" element={<CMSTerms />} />
      </Routes>
    </CMSLayout>
  );
};

export default App;
